import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import BetaLabel from 'core/elements/badge/BetaLabel'
import Text from 'core/elements/Text'
import Theme from 'core/themes/model'
import generateTestId from 'utils/test-helpers'

interface Props {
  value: string
  label: string
  count?: number
  beta?: boolean
  isActive: boolean
  onClick: (value: string) => void
}

export default function TabPreview({ label, value, isActive, onClick, count, beta }: Props) {
  const classes = useStyles()
  return (
    <div className={classes.tabPreview} onClick={() => onClick(value)}>
      <div className={classes.tabLabel}>
        <Text
          className={clsx(classes.tabText, { [classes.tabTextActive]: isActive })}
          data-testid={generateTestId(label, 'tab')}
          variant="subtitle2"
        >
          {beta ? <BetaLabel text={label} /> : label} {count !== undefined ? `(${count})` : ''}
        </Text>
      </div>
      {isActive && <span className={classes.activeBorder} />}
    </div>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  tabPreview: {
    height: 35,
    display: 'grid',
    gap: 8,
    gridAutoFlow: 'row',
    cursor: 'pointer',
  },
  // Owns the side padding. Fixed to the label's own line height (subtitle2: 24px in both themes)
  // so a taller Beta badge can't grow this row and push the text above the other tabs' text.
  tabLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    height: 24,
    padding: '0 16px',
    // Hover from the whole row, not just the text, so the side padding still highlights it
    '&:hover $tabText': {
      color: theme.components.tab.activeText,
    },
  },
  tabText: {
    color: theme.components.tab.text,
  },
  tabTextActive: {
    color: theme.components.tab.activeText,
  },
  activeBorder: {
    height: 4,
    display: 'grid',
    borderRadius: 1.5,
    margin: 0,
    padding: 0,
    background: theme.components.tab.activeBackground,
  },
}))
